/**
 * Login — dark editorial surface, continuous voice from the landing hero.
 *
 * This is the post-landing entry point. On juuniai.com (apex) it's reached
 * via the nav's "Sign in" link; on tenant subdomains (deltra.juuniai.com)
 * it's also reachable directly via bookmark. The surface is ink (#14110c)
 * with a currentColor paper-grain overlay — the same treatment that opens
 * the landing hero at scrollY=0. After authentication, the app routes by
 * role via getPostAuthLandingPath(user): admin / SPL manager surfaces
 * transition to the light paper Product System; annotator / specialist
 * surfaces stay in the dark editorial voice.
 *
 * Role tabs (annotator / ops / client / founder) that the previous
 * version of this page carried have been removed — they were cosmetic
 * only, since post-auth routing is already server-driven. Dropping them
 * simplified the form from 309 to ~200 lines and removed color-theme
 * fighting between the four role palettes.
 *
 * Tenant chip: on any *.juuniai.com that isn't the apex, a small chip
 * carrying the subdomain renders next to the JUUNI 十二 brand ("DELTRA",
 * "ACME", etc.). Reassures tenant staff that they're logging into the
 * right tenant portal.
 *
 * Prop signatures and auth flows preserved — only the presentation layer
 * changed. Screen-reader behavior is unaltered.
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPostAuthLandingPath } from '../services/authBootstrapService';
import Footer from '../components/Footer';

/**
 * Extract a human-readable tenant token from the hostname. Returns null
 * on apex juuniai.com, www.juuniai.com, localhost, or anything that
 * doesn't match *.juuniai.com.
 *
 *   deltra.juuniai.com          → "DELTRA"
 *   acme-labs.juuniai.com       → "ACME-LABS"
 *   juuniai.com / www.juuniai.com → null
 *   localhost / vercel previews → null
 */
function tenantLabelFromHost(hostname) {
  if (!hostname || !hostname.endsWith('juuniai.com')) return null;
  if (hostname === 'juuniai.com' || hostname === 'www.juuniai.com') return null;
  const sub = hostname.slice(0, -'juuniai.com'.length).replace(/\.$/, '');
  if (!sub) return null;
  return sub.toUpperCase();
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantLabel, setTenantLabel] = useState(null);
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setTenantLabel(tenantLabelFromHost(window.location.hostname));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { user } = await signIn(email, password);
      const landingPath = await getPostAuthLandingPath(user);
      navigate(landingPath);
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // Previously this used a per-role redirectPath map, but since
      // getPostAuthLandingPath is the canonical role→path resolver
      // (queried on the post-redirect callback), the redirect path
      // doesn't need to be role-aware here. Land on / and the
      // ProtectedRoute tree will finish routing once auth settles.
      await signInWithGoogle({ redirectPath: '/' });
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    }
  };

  // Color palette — inline custom properties so the Login page is
  // self-contained (doesn't depend on the landing's tokens.css being
  // loaded on a given Vercel build). Matches the dark-hero voice.
  const theme = {
    surface:         '#14110c',   // ink
    onSurface:       '#efe8d6',   // ink-rev (warm light)
    onSurface2:      '#c3b8a3',   // muted warm
    onSurfaceMuted:  '#8a847a',
    hair:            'rgba(239, 232, 214, 0.14)',
    hair2:           'rgba(239, 232, 214, 0.28)',
    accent:          '#7a1f1f',   // burgundy
    accent2:         '#b84a3a',
    positive:        '#2c6b43',   // deep forest green
    paper:           '#eee7d8',   // inverted surface (for button)
  };

  const fontSans  = "'Geist', 'Inter', -apple-system, system-ui, sans-serif";
  const fontMono  = "'JetBrains Mono', 'SF Mono', monospace";
  const fontBrush = "'Yuji Boku', 'Yuji Syuku', 'Noto Serif JP', serif";

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontFamily: fontSans,
    fontWeight: 500,
    color: theme.onSurfaceMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.22em',
    marginBottom: 10,
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 14px',
    background: 'transparent',
    border: `1px solid ${theme.hair2}`,
    borderRadius: 2,
    fontSize: 14,
    fontFamily: fontSans,
    color: theme.onSurface,
    outline: 'none',
    boxSizing: 'border-box',
    letterSpacing: '0.01em',
    transition: 'border-color 180ms ease',
  };

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',  // Phase 14 audit fix — iOS Safari address-bar truncation guard
        background: theme.surface,
        color: theme.onSurface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: fontSans,
        overflow: 'hidden',
      }}
    >
      {/* Paper grain overlay — currentColor dots on ink surface.
          Matches the landing hero's subtle texture. Kept low-opacity
          so it reads as paper warmth, not visible noise. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          color: theme.onSurface,
          backgroundImage: `radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)`,
          backgroundSize: '3px 3px, 7px 7px',
          backgroundPosition: '0 0, 1px 1px',
          opacity: 0.035,
          zIndex: 0,
        }}
      />

      {/* Nav — just the home link, mirrors the landing's brand position. */}
      <nav
        aria-label="Login chrome"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '18px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 2,
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: theme.onSurfaceMuted,
          pointerEvents: 'none',
        }}
      >
        <a
          href="/"
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 12,
            fontWeight: 600,
            letterSpacing: '0.28em',
            fontSize: 14,
            color: theme.onSurface,
            textDecoration: 'none',
            pointerEvents: 'auto',
          }}
        >
          JUUNI
          <span style={{ fontFamily: fontBrush, fontSize: 20, letterSpacing: 0, fontWeight: 400, color: theme.onSurfaceMuted }}>
            十二
          </span>
        </a>
        <div style={{ fontFamily: fontMono, letterSpacing: '0.08em', fontSize: 12, pointerEvents: 'auto' }}>
          juuniai.com
        </div>
      </nav>

      {/* Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 440,
          padding: 44,
          background: 'transparent',
          border: `1px solid ${theme.hair2}`,
          borderRadius: 2,
          boxSizing: 'border-box',
        }}
      >
        {/* Brand lockup — JUUNI 十二 + optional tenant chip + caption.
            Phase 14.7 — wrapped in <h1> so the page has a semantic
            heading. Visual styling preserved via inline span; the h1
            itself is unstyled. WCAG 2.4.6 / 1.3.1. */}
        <div style={{ marginBottom: 36 }}>
          <h1 style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14, margin: '0 0 14px', fontWeight: 'normal' }}>
            <span
              style={{
                fontFamily: fontSans,
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: '0.28em',
                color: theme.onSurface,
              }}
            >
              JUUNI
            </span>
            <span
              style={{
                fontFamily: fontBrush,
                fontSize: 26,
                fontWeight: 400,
                letterSpacing: 0,
                color: theme.onSurfaceMuted,
                lineHeight: 1,
              }}
            >
              十二
            </span>
            {tenantLabel && (
              <span
                aria-label={`Tenant: ${tenantLabel}`}
                style={{
                  marginLeft: 'auto',
                  padding: '4px 10px',
                  border: `1px solid ${theme.hair2}`,
                  borderRadius: 2,
                  fontFamily: fontMono,
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  color: theme.onSurface2,
                  textTransform: 'uppercase',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {tenantLabel}
              </span>
            )}
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: fontSans,
              fontSize: 13,
              lineHeight: 1.6,
              color: theme.onSurface2,
              letterSpacing: '0.01em',
            }}
          >
            The operating system for annotation companies.
          </p>
        </div>

        {/* Error banner — accent left-edge, hairline-bordered paper surface.
            Mirrors the landing's AlertBanner treatment. */}
        {error && (
          <div
            role="alert"
            style={{
              position: 'relative',
              padding: '12px 14px 12px 18px',
              marginBottom: 20,
              borderLeft: `3px solid ${theme.accent2}`,
              background: 'rgba(184, 74, 58, 0.08)',
              border: `1px solid ${theme.hair2}`,
              borderLeftWidth: 3,
              borderRadius: 2,
              color: theme.onSurface,
              fontSize: 13,
              fontFamily: fontSans,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="login-email" style={labelStyle}>Email</label>
            <input
              id="login-email"
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              maxLength={254}
              style={inputStyle}
              onFocus={(e) => { e.currentTarget.style.borderColor = theme.onSurface; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = theme.hair2; }}
            />
          </div>

          <div style={{ marginBottom: 28 }}>
            <label htmlFor="login-password" style={labelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                name="password"
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                maxLength={72}
                style={{ ...inputStyle, paddingRight: 64 }}
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.onSurface; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.hair2; }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: theme.onSurfaceMuted,
                  fontFamily: fontMono,
                  padding: '6px 8px',
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 0',
              background: theme.paper,
              color: theme.surface,
              border: `1px solid ${theme.paper}`,
              borderRadius: 2,
              fontFamily: fontSans,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'background 120ms ease, border-color 120ms ease, opacity 150ms',
            }}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = theme.accent2; e.currentTarget.style.borderColor = theme.accent2; e.currentTarget.style.color = theme.paper; } }}
            onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.background = theme.paper; e.currentTarget.style.borderColor = theme.paper; e.currentTarget.style.color = theme.surface; } }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          {/* Phase 16.2 (2026-04-28): forgot-password link */}
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <Link
              to="/auth/reset"
              style={{
                fontSize: 12,
                fontFamily: fontMono,
                color: theme.onSurfaceMuted,
                textDecoration: 'none',
                letterSpacing: '0.05em',
              }}
            >
              Forgot password?
            </Link>
          </div>
        </form>

        {/* Divider */}
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            margin: '28px 0 24px',
          }}
        >
          <div style={{ flex: 1, height: 1, background: theme.hair }} />
          <span
            style={{
              fontSize: 10,
              fontFamily: fontMono,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: theme.onSurfaceMuted,
            }}
          >
            or
          </span>
          <div style={{ flex: 1, height: 1, background: theme.hair }} />
        </div>

        {/* Google OAuth — secondary chip, hairline border on ink surface */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          style={{
            width: '100%',
            padding: '12px 0',
            background: 'transparent',
            color: theme.onSurface,
            border: `1px solid ${theme.hair2}`,
            borderRadius: 2,
            fontFamily: fontSans,
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            transition: 'border-color 120ms ease, background 120ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = theme.onSurface; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = theme.hair2; }}
        >
          <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Signup link */}
        <div
          style={{
            textAlign: 'center',
            marginTop: 28,
            fontSize: 12,
            color: theme.onSurfaceMuted,
            fontFamily: fontSans,
            letterSpacing: '0.02em',
          }}
        >
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: theme.onSurface,
              fontFamily: fontSans,
              fontSize: 12,
              letterSpacing: '0.02em',
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Sign up
          </button>
        </div>
      </div>

      {/* Phase 11 slot 15: legal footer links — public entry point must
          surface Terms + Privacy at minimum (pre-cutover audit). */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 12,
          fontFamily: fontMono,
          letterSpacing: '0.05em',
          color: theme.onSurfaceMuted,
          zIndex: 2,
          display: 'flex',
          gap: 14,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Link to="/legal/terms" style={{ color: theme.onSurfaceMuted, textDecoration: 'none' }}>Terms</Link>
        <span aria-hidden="true">·</span>
        <Link to="/legal/privacy" style={{ color: theme.onSurfaceMuted, textDecoration: 'none' }}>Privacy</Link>
        <span aria-hidden="true">·</span>
        <Link to="/docs" style={{ color: theme.onSurfaceMuted, textDecoration: 'none' }}>Docs</Link>
      </div>

      {/* Bottom brand signoff — echoes the landing's hero-meta strip */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 28,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: 10,
          fontFamily: fontBrush,
          letterSpacing: '0.2em',
          color: theme.onSurface2,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <span>正確に測る</span>
        <span style={{ fontFamily: fontMono, marginLeft: 14, letterSpacing: '0.25em' }}>· MEASURE PRECISELY</span>
      </div>
      {/* Phase 19.4 audit-fix (2026-04-29): legal/security links footer per scope §6 + GDPR Article 13.
          Absolute-positioned at page bottom so it doesn't disrupt the centered card. */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <Footer variant="auth" />
      </div>
    </div>
  );
}
